import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { intelligenceReportsApi } from '../api/intelligenceReportsApi';
import { useToast } from '../../../shared/components/toast/useToast';
import type {
  GenerateReportPayload,
  ListReportsQuery,
  UpsertAnalyticsConfigPayload,
} from '../types';

const KEY = 'intelligence-reports';

/** Nest validation errors arrive as `message: string[]`; take the first line. */
const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

export const useIntelligenceReports = (query: ListReportsQuery = {}) =>
  useQuery({
    queryKey: [KEY, 'list', query],
    queryFn: () => intelligenceReportsApi.list(query),
    staleTime: 60 * 1000,
  });

export const useIntelligenceReport = (id: string | undefined) =>
  useQuery({
    queryKey: [KEY, 'detail', id],
    queryFn: () => intelligenceReportsApi.get(id as string),
    enabled: !!id,
    staleTime: 60 * 1000,
  });

/**
 * Per-merchant analytics config. Also the provenance oracle: without it the UI
 * cannot tell "no property ID configured" from "the upstream call failed", so
 * the detail page always loads it alongside the report.
 */
export const useAnalyticsConfig = (merchantId: string | undefined) =>
  useQuery({
    queryKey: [KEY, 'config', merchantId],
    queryFn: () => intelligenceReportsApi.getConfig(merchantId as string),
    enabled: !!merchantId,
    staleTime: 60 * 1000,
  });

/**
 * Generation is synchronous server-side despite the 202 — it fans out to four
 * analytics APIs and then (when enabled) Vertex AI, so it can take a while.
 * A 409 means a report already exists for that period; the caller turns that
 * into a regenerate confirmation rather than a toast, so it is rethrown quietly.
 */
export const useGenerateReport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (payload: GenerateReportPayload) => intelligenceReportsApi.generate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'list'] });
      toast.success('Report generated and placed in Pending review');
    },
    onError: (err: unknown) => {
      // 409 is handled by the caller as a "regenerate?" prompt. ApiFetch has
      // already toasted the server message for every status, so re-toasting
      // here would double up — only add a message when we have a better one.
      if ((err as { status?: number })?.status === 409) return;
      if (!(err as { toasted?: boolean })?.toasted) {
        toast.error(errMsg(err, 'Failed to generate report'));
      }
    },
  });
};

/**
 * Approve, optionally with edited copy. Invalidates the detail as well as the
 * list because approving can mint a new RELEASED version row.
 */
export const useApproveReport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, content }: { id: string; content?: string }) =>
      intelligenceReportsApi.approve(id, content),
    onSuccess: (_res, { id }) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'detail', id] });
      queryClient.invalidateQueries({ queryKey: [KEY, 'list'] });
      toast.success('Report approved — not yet visible to the merchant');
    },
    onError: (err: unknown) => {
      if (!(err as { toasted?: boolean })?.toasted) {
        toast.error(errMsg(err, 'Failed to approve report'));
      }
    },
  });
};

export const useReleaseReport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => intelligenceReportsApi.release(id),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'detail', id] });
      queryClient.invalidateQueries({ queryKey: [KEY, 'list'] });
      toast.success('Report released — the merchant can now see it');
    },
    onError: (err: unknown) => {
      if (!(err as { toasted?: boolean })?.toasted) {
        toast.error(errMsg(err, 'Failed to release report'));
      }
    },
  });
};

export const useUpsertAnalyticsConfig = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({
      merchantId,
      payload,
    }: {
      merchantId: string;
      payload: UpsertAnalyticsConfigPayload;
    }) => intelligenceReportsApi.upsertConfig(merchantId, payload),
    onSuccess: (_res, { merchantId }) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'config', merchantId] });
      toast.success('Analytics configuration saved');
    },
    onError: (err: unknown) => {
      if (!(err as { toasted?: boolean })?.toasted) {
        toast.error(errMsg(err, 'Failed to save analytics configuration'));
      }
    },
  });
};
